import Reflux from "reflux";
import _ from "lodash";
import AppConstants from "../constants/AppConstants";
import SiteActions from "../actions/SiteActions";
import DocumentStore from "../document/DocumentStore";
import { COMMANDS } from "../document/commands";
import SiteDesignStore from "./SiteDesignStore";
import SelectionStore from "./SelectionStore";
import ElementUtils from "../utils/JS/ElementUtils";
import BlockUpdateVO from "../vo/BlockUpdate";
import ElementGroupUtils from "../utils/ElementGroupUtils";
import GroupStore from "./GroupStore";

/**
 * @typedef {import("immutable").Map} ImmutableMap
 */

/**
 * Repositions elements so the top-left of their bounding box lands at the given canvas position.
 * @param {Object[]} elements - Elements with desktop/mobile x,y coordinates
 * @param {{ desktop: { x: number, y: number }, mobile: { x: number, y: number } }} canvasPosition
 */
const repositionElementsToCanvasPosition = (elements, canvasPosition) => {
  const [minDesktopX, minDesktopY, minMobileX, minMobileY] = elements.reduce(
    (acc, element, i) => {
      if (i === 0) {
        return [element.desktop.x, element.desktop.y, element.mobile.x, element.mobile.y];
      }
      return [
        Math.min(acc[0], element.desktop.x),
        Math.min(acc[1], element.desktop.y),
        Math.min(acc[2], element.mobile.x),
        Math.min(acc[3], element.mobile.y),
      ];
    },
    [0, 0, 0, 0],
  );

  const offsetDesktopX = canvasPosition.desktop.x - minDesktopX;
  const offsetDesktopY = canvasPosition.desktop.y - minDesktopY;
  const offsetMobileX = canvasPosition.mobile.x - minMobileX;
  const offsetMobileY = canvasPosition.mobile.y - minMobileY;

  elements.forEach((element) => {
    element.desktop.x += offsetDesktopX;
    element.desktop.y += offsetDesktopY;
    element.mobile.x += offsetMobileX;
    element.mobile.y += offsetMobileY;
  });
};

const ClipboardStore = Reflux.createStore({
  storeName: 'ClipboardStore',
  listenables: [SiteActions],

  init() {
    this.clipboard = null;
    // tracks if a copy came from a group
    this.fromContained = false;
  },

  onCopy(data) {
    const cut = _.get(data, "cut", false);
    _.unset(data, "cut");

    this.fromContained = false;

    if (_.isEmpty(data)) {
      const elements = SiteDesignStore.findSelectedElements({ asJS: true }).map((element) => {
        const { pId: containerId, id } = element;

        // get released element if contained
        if (containerId) {
          if (!this.fromContained) this.fromContained = true;
          const blockData = SelectionStore.getBlock();
          const block = SiteDesignStore.getBlockContainer().getBlockEntity(blockData.get("id"));
          const elementContainerPath = block.findElementContainerEntityById(containerId);
          const elementContainer = block.getElementContainerEntity(elementContainerPath);
          return elementContainer.getTranslatedOut(id).toJS();
        }

        return element;
      });

      const clipboardData = {
        type: AppConstants.ITEM_TYPE_ELEMENT,
        payload: elements,
        label: "Element(s)",
        layout: SelectionStore.getLayout(),
      };

      this.clipboard = clipboardData;

      if (cut) {
        DocumentStore.dispatch({ type: COMMANDS.REMOVE_SELECTED_ELEMENTS });
      }
    } else if (data.type === AppConstants.ITEM_TYPE_BLOCK && !_.isEmpty(data.id)) {
      const block = SiteDesignStore.getBlock(data.id);

      if (block) {
        const blockJS = block.toJS();

        const clipboardData = {
          type: AppConstants.ITEM_TYPE_BLOCK,
          payload: blockJS,
          label: blockJS.name,
        };

        this.clipboard = clipboardData;

        if (cut) {
          DocumentStore.dispatch({ type: COMMANDS.REMOVE_BLOCK, payload: { block } });
        }
      }
    }

    if (this.clipboard) {
      this.trigger(_.pick(data, ["type,label"]));
    }
  },

  /**
   * Pastes an element or block
   * @param {Object} props
   * @param {(number|false)=} props.insertAt - Insert at a specific index
   * @param {string=} props.type - Type of thing being pasted
   * @param {boolean=} props.select - Select the pasted element
   * @param {Object=} props.canvasPosition - Position to paste at (optional)
   */
  onPaste({
    insertAt = false,
    type: pasteType = AppConstants.ITEM_TYPE_ELEMENT,
    select,
    canvasPosition,
  } = {}) {
    const page = SiteDesignStore.getCurrentPage();
    const isWpPage = page.data?.has("wp");

    if (this.clipboard) {
      if (
        pasteType === AppConstants.ITEM_TYPE_ELEMENT &&
        this.clipboard.type === AppConstants.ITEM_TYPE_ELEMENT
      ) {
        let processedElements = ElementGroupUtils.processElements(this.clipboard.payload);
        const containedBy = GroupStore.getGroupSelectionId();

        // reverse element order if group to group paste
        containedBy && processedElements.reverse();

        if (canvasPosition) {
          repositionElementsToCanvasPosition(processedElements, canvasPosition);
        }

        DocumentStore.dispatch({
          type: COMMANDS.ADD_ELEMENT,
          payload: {
            containedBy,
            elements: isWpPage
              ? processedElements
              : processedElements.map((element) => {
                  const { link, wp, ...rest } = element;
                  return link?.type === "wp_post" || wp ? { ...rest } : element;
                }),
            select,
            maintainPosition: !!canvasPosition,
          },
        });
      } else if (
        pasteType === AppConstants.ITEM_TYPE_BLOCK &&
        this.clipboard.type === AppConstants.ITEM_TYPE_BLOCK
      ) {
        const action = {
          block: this.clipboard.payload,
        };

        if (insertAt !== false) {
          action.insertAt = insertAt;
        }

        DocumentStore.dispatch({ type: COMMANDS.ADD_BLOCK, payload: action });
      }
    }
  },

  onPasteInto(data) {
    var pasteType = _.get(data, "type", AppConstants.ITEM_TYPE_ELEMENT);
    if (this.clipboard) {
      if (
        pasteType == AppConstants.ITEM_TYPE_BLOCK &&
        this.clipboard.type == AppConstants.ITEM_TYPE_BLOCK
      ) {
        DocumentStore.dispatch({
          type: COMMANDS.COPY_BLOCK_INTO_BLOCK,
          payload: { id: data.id, block: this.clipboard.payload },
        });
      }
    }
  },

  // NOTE: extra non specific property pasting is only across same element types
  /**
   * 
   * @param {ImmutableMap} theme 
   */
  onSpecialPaste(theme) {
    if (this.clipboard?.payload?.length > 1 || !this?.clipboard?.payload) {
      return;
    }

    const targetElements = SelectionStore.getFullElementsJS();
    const copiedElementType = this._copiedElement().type;
    const blockId = targetElements[0]?.selected?.blockId;

    const updates = targetElements.flatMap((targetElement) => {
      const updator = new BlockUpdateVO();
      this.pasteCommonProperties([targetElement], updator);
      if (targetElement.type === copiedElementType) {
        this.pasteTypeSpecificProperties([targetElement], updator, theme);
      }
      return updator.updates;
    });

    const updatesObject = {
      id: blockId,
      updates,
    };

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: updatesObject });
  },

  /**
   * 
   * @param {Array<import("@showitinc/merge-utils").SyncableEntity>} targetElements 
   * @param {*} updator 
   * @param {ImmutableMap} theme 
   */
  pasteTypeSpecificProperties(targetElements, updator, theme) {
    if (targetElements.length > 0) {
      switch (this._copiedElement().type) {
        case AppConstants.ELEMENT_TYPE_TEXT:
        case AppConstants.ELEMENT_TYPE_SIMPLE:
        case AppConstants.ELEMENT_TYPE_LINE:
        case AppConstants.ELEMENT_TYPE_ICON:
        case AppConstants.ELEMENT_TYPE_SOCIAL_GRID:
        case AppConstants.ELEMENT_TYPE_GALLERY:
          ElementUtils.handleCopy(
            this._copiedElement(),
            this.clipboard.layout,
            targetElements,
            SelectionStore.getLayout(),
            updator,
            theme
          );
          break;
        default:
      }
    }
  },

  // pastes properties in common with all element types
  pasteCommonProperties(targetElements, updator) {
    if (targetElements.length <= 0) {
      return;
    }

    const props = [
      this._copiedElement(),
      this.clipboard.layout,
      targetElements,
      SelectionStore.getLayout(),
      updator,
    ];

    ElementUtils.setEffects(...props);
    ElementUtils.setHover(targetElements, this._copiedElement().hover, updator);
    ElementUtils.setTransitions(...props);
  },

  getContentLabel() {
    var label = "";
    if (this.clipboard != null) {
      label = this.clipboard.label;
    }

    return label;
  },

  canPasteBlock() {
    return this.clipboard != null && this.clipboard.type == "block";
  },

  canPasteElements() {
    const type = this.clipboard?.type;
    return this.clipboard !== null && (type === "element" || type === "elements") && this.clipboard;
  },

  _copiedElement() {
    return this.clipboard.payload[0];
  },
});

export default ClipboardStore;
